import { getAuthToken, getBidderId } from './user';
import { postLAPayment, postStripePayment } from '@/redux/api/payment';
import { transformAddressForStripe, translateAddressAccents } from '@/utils/address';
import { $Values } from 'utility-types';
import { Address } from '@liveauctioneers/caterwaul-components/types/Address';
import { createSelector } from 'reselect';
import { DISMISS_MODAL } from './actions';
import { fetchWonItemsIfNeeded } from './wonItems';
import { getDeployment } from './config';
import { getSessionId } from '@/redux/modules/user';
import { getSignifydSessionId } from './signifyd';
import { getStripeErrorMessageFromResponse } from '@/utils/getStripeErrorMessageFromResponse';
import { GlobalState } from '../rootReducer';
import paymentErrorCodes from '@/enums/paymentErrorCodes';
import { paymentMethods } from '@/enums';
import paymentProviders from '@/enums/paymentProviders';
import { StripeBillingAddress } from '@/types/StripeBillingAddress';

/* Action Types */
export const SUBMIT_PAYMENT_FAIL_STRIPE = 'la/ui/payment/FAIL_STRIPE';
export const SUBMIT_PAYMENT_FAIL = 'la/ui/payment/FAIL';
export const SUBMIT_PAYMENT_REQUEST = 'la/ui/payment/REQUEST';
export const SUBMIT_PAYMENT_SUCCESS = 'la/ui/payment/SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: false,
    errorCode: undefined,
    errorMessage: null,
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE & {
    errorCode?: $Values<typeof paymentErrorCodes>;
    errorMessage: string | null | undefined;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case SUBMIT_PAYMENT_REQUEST:
            return {
                ...state,
                error: false,
                errorCode: undefined,
                errorMessage: null,
                submitted: true,
                success: false,
            };
        case SUBMIT_PAYMENT_SUCCESS:
            return {
                ...state,
                submitted: false,
                success: true,
            };
        case SUBMIT_PAYMENT_FAIL_STRIPE:
            return {
                ...state,
                error: true,
                errorMessage: getStripeErrorMessageFromResponse(action.payload),
                submitted: false,
                success: false,
            };
        case SUBMIT_PAYMENT_FAIL:
            return {
                ...state,
                error: true,
                errorCode: action.payload,
                submitted: false,
                success: false,
            };
        case DISMISS_MODAL:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.payment;

export const getUiSubmitPayment = createSelector(stateSelector, (state) => state);

/* ACTION CREATORS */
type SubmitPaymentParams = {
    amount?: number;
    cardToken?: string;
    catalogId?: number;
    currency?: string;
    invoiceId?: string;
    paymentMethod: $Values<typeof paymentMethods>;
    paymentToken?: string;
    providerId: number;
    shippingAddress?: Address;
};
export const submitPayment = ({
    shippingAddress,
    amount = 0,
    cardToken = '',
    catalogId = 0,
    currency = '',
    invoiceId = '',
    paymentMethod = paymentMethods.CREDIT_CARD,
    paymentToken = '',
    providerId = paymentProviders.STRIPE,
}: SubmitPaymentParams) => async (dispatch: Function) => {
    // translate accents because mainhost
    const translatedAddress = translateAddressAccents(shippingAddress);

    if (providerId === paymentProviders.STRIPE) {
        const stripeAddress = transformAddressForStripe(translatedAddress);
        await dispatch(submitStripePayment({ address: stripeAddress, amount, cardToken, catalogId, currency }));
    } else {
        await dispatch(submitLAPayment({ address: translatedAddress, invoiceId, paymentMethod, paymentToken }));
    }
};

type submitStripePaymentParams = {
    address: StripeBillingAddress;
    amount: number;
    cardToken: string;
    catalogId: number;
    currency: string;
};
const submitStripePayment = ({ address, amount, cardToken, catalogId, currency }: submitStripePaymentParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);
        const riskifiedSessionId = getSessionId();
        const signifydSessionId = getSignifydSessionId(state);

        dispatch({
            payload: bidderId,
            type: SUBMIT_PAYMENT_REQUEST,
        });
        const response = await postStripePayment({
            address,
            amount,
            authToken,
            bidderId,
            cardToken,
            catalogId,
            currency,
            deployment,
            riskifiedSessionId,
            signifydSessionId,
        });

        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: SUBMIT_PAYMENT_SUCCESS,
        });
        return dispatch(fetchWonItemsIfNeeded({ force: true, page: 1, pageSize: 1250 }));
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: SUBMIT_PAYMENT_FAIL_STRIPE,
        });
        throw error;
    }
};

type submitLAPaymentParams = {
    address: Address;
    invoiceId: string;
    paymentMethod: $Values<typeof paymentMethods>;
    paymentToken?: string;
};
export const submitLAPayment = ({ address, invoiceId, paymentMethod, paymentToken }: submitLAPaymentParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);
        const riskifiedSessionId = getSessionId();
        const signifydSessionId = getSignifydSessionId(state);

        dispatch({
            payload: bidderId,
            type: SUBMIT_PAYMENT_REQUEST,
        });
        const response = await postLAPayment({
            address,
            authToken,
            bidderId,
            deployment,
            invoiceId,
            paymentMethod,
            paymentToken,
            riskifiedSessionId,
            signifydSessionId,
        });

        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: SUBMIT_PAYMENT_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: SUBMIT_PAYMENT_FAIL,
        });
        throw error;
    }
};
